import { useMemo, useRef, useEffect } from 'react';
import { 
  format, 
  parseISO, 
  isSameDay, 
  isToday, 
  startOfWeek, 
  addDays,
  isWeekend
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { Video } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface CalendarEvent {
  id: string;
  subject: string;
  start_time: string;
  end_time: string;
  location?: string;
  teams_join_url?: string;
  is_all_day?: boolean;
}

interface CalendarWeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
}

const HOUR_HEIGHT = 48; // pixels per hour (smaller for week view)
const START_HOUR = 6;
const END_HOUR = 22;

function getEventPosition(event: CalendarEvent) {
  try {
    const start = parseISO(event.start_time);
    const end = parseISO(event.end_time);
    
    const startMinutes = Math.max(start.getHours() * 60 + start.getMinutes(), START_HOUR * 60);
    const endMinutes = Math.min(end.getHours() * 60 + end.getMinutes(), END_HOUR * 60);
    const duration = Math.max(endMinutes - startMinutes, 30);
    
    const top = ((startMinutes - START_HOUR * 60) / 60) * HOUR_HEIGHT;
    const height = Math.max((duration / 60) * HOUR_HEIGHT, 20);
    
    return { top, height, startMinutes, endMinutes };
  } catch {
    return null;
  }
}

export function CalendarWeekView({ currentDate, events, onEventClick, onDayClick }: CalendarWeekViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  
  // Get week days (Monday to Sunday)
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  // Auto-scroll to current time or 8am
  useEffect(() => {
    if (scrollRef.current) {
      const scrollTo = isToday(currentDate) ? currentMinutes : 8 * 60;
      const scrollPosition = ((scrollTo - START_HOUR * 60) / 60) * HOUR_HEIGHT - 50;
      scrollRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [currentDate]);

  // Group events by day
  const eventsByDay = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {};
    
    weekDays.forEach(day => {
      const dayKey = format(day, 'yyyy-MM-dd');
      grouped[dayKey] = events.filter(event => {
        if (!event.start_time) return false;
        try {
          const start = parseISO(event.start_time);
          return isSameDay(start, day) && !event.is_all_day;
        } catch {
          return false;
        }
      });
    });
    
    return grouped;
  }, [events, weekDays]);

  // All-day events for the week
  const allDayEvents = useMemo(() => {
    return weekDays.map(day => ({
      day,
      events: events.filter(event => {
        if (!event.start_time) return false;
        try {
          const start = parseISO(event.start_time);
          return isSameDay(start, day) && event.is_all_day;
        } catch {
          return false;
        }
      })
    }));
  }, [events, weekDays]);

  const hasAllDayEvents = allDayEvents.some(d => d.events.length > 0);
  const hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

  return (
    <div className="flex flex-col h-[600px] bg-card rounded-xl border border-border overflow-hidden">
      {/* Week header */}
      <div className="flex-shrink-0 border-b border-border">
        <div className="grid grid-cols-8">
          {/* Time column header */}
          <div className="border-r border-border bg-muted/30 p-2" />
          
          {/* Day headers */}
          {weekDays.map(day => (
            <div
              key={day.toISOString()}
              onClick={() => onDayClick?.(day)}
              className={cn(
                "p-2 text-center border-r border-border last:border-r-0 cursor-pointer transition-colors",
                isToday(day) && "bg-energy-green/10",
                isWeekend(day) && "bg-muted/30",
                "hover:bg-muted/50"
              )}
            >
              <div className="text-xs font-medium text-muted-foreground uppercase">
                {format(day, 'EEE', { locale: nl })}
              </div>
              <div className={cn(
                "mt-1 w-8 h-8 mx-auto flex items-center justify-center rounded-full text-sm font-semibold",
                isToday(day) 
                  ? "bg-energy-green text-white" 
                  : "text-foreground"
              )}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>

        {/* All-day events row */}
        {hasAllDayEvents && (
          <div className="grid grid-cols-8 border-t border-border bg-muted/20">
            <div className="border-r border-border p-1 text-xs text-muted-foreground text-center">
              Hele dag
            </div>
            {allDayEvents.map(({ day, events: dayAllDay }) => (
              <div
                key={day.toISOString()}
                className="border-r border-border last:border-r-0 p-1 min-h-[28px]"
              >
                {dayAllDay.slice(0, 2).map(event => (
                  <div
                    key={event.id}
                    onClick={() => onEventClick?.(event)}
                    className="text-xs px-1.5 py-0.5 rounded bg-energy-green/20 text-energy-green truncate cursor-pointer hover:bg-energy-green/30 mb-0.5"
                  >
                    {event.subject}
                  </div>
                ))}
                {dayAllDay.length > 2 && (
                  <div className="text-xs text-muted-foreground px-1">
                    +{dayAllDay.length - 2} meer
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Time grid */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="grid grid-cols-8" style={{ height: (END_HOUR - START_HOUR) * HOUR_HEIGHT }}>
          {/* Time labels column */}
          <div className="relative border-r border-border bg-muted/10">
            {hours.map(hour => (
              <div
                key={hour}
                className="absolute left-0 right-0 border-t border-border/50"
                style={{ top: (hour - START_HOUR) * HOUR_HEIGHT }}
              >
                <span className="absolute -top-2.5 left-1 text-xs text-muted-foreground font-mono bg-card px-1">
                  {String(hour).padStart(2, '0')}:00
                </span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map(day => {
            const dayKey = format(day, 'yyyy-MM-dd');
            const dayEvents = eventsByDay[dayKey] || [];
            const showCurrentTimeLine = isToday(day) && currentMinutes >= START_HOUR * 60 && currentMinutes <= END_HOUR * 60;
            
            return (
              <div
                key={dayKey}
                className={cn(
                  "relative border-r border-border last:border-r-0",
                  isWeekend(day) && "bg-muted/20"
                )}
              >
                {/* Hour lines */}
                {hours.map(hour => (
                  <div
                    key={hour}
                    className="absolute left-0 right-0 border-t border-border/30"
                    style={{ top: (hour - START_HOUR) * HOUR_HEIGHT }}
                  />
                ))}

                {/* Current time line */}
                {showCurrentTimeLine && (
                  <div
                    className="absolute left-0 right-0 flex items-center z-20"
                    style={{ top: ((currentMinutes - START_HOUR * 60) / 60) * HOUR_HEIGHT }}
                  >
                    <div className="w-2 h-2 rounded-full bg-energy-orange" />
                    <div className="flex-1 h-0.5 bg-energy-orange" />
                  </div>
                )}

                {/* Events */}
                {dayEvents.map(event => {
                  const position = getEventPosition(event);
                  if (!position) return null;
                  
                  return (
                    <div
                      key={event.id}
                      onClick={() => onEventClick?.(event)}
                      className={cn(
                        "absolute left-0.5 right-0.5 rounded px-1 py-0.5 cursor-pointer overflow-hidden",
                        "bg-gradient-to-r from-energy-green to-energy-green/80",
                        "hover:from-energy-green hover:to-energy-green",
                        "border-l-2 border-energy-green",
                        "shadow-sm hover:shadow transition-shadow"
                      )}
                      style={{
                        top: position.top,
                        height: position.height,
                      }}
                    >
                      <div className="flex items-start gap-1 h-full">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-white truncate">
                            {event.subject}
                          </div>
                          {position.height > 30 && (
                            <div className="text-[10px] text-white/80">
                              {format(parseISO(event.start_time), 'HH:mm')}
                            </div>
                          )}
                        </div>
                        {event.teams_join_url && (
                          <Video className="h-3 w-3 text-energy-blue flex-shrink-0 mt-0.5" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
